import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiRequestsService } from '../../services/api-requests.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  // ── Sign-in state ────────────────────────────────────────────────────────
  activeTab: 'signin' | 'onboard' = 'signin';
  clientCode = '';
  userName = '';
  pwd = '';
  isLoggingIn = false;
  errorMessage = '';

  // ── Onboarding state ─────────────────────────────────────────────────────
  metaAppId = '';
  metaConfigId = '';
  fbSdkLoaded = false;
  fbInitialized = false;
  newClientCode = '';
  newDbName = '';
  capturedWabaId = '';
  capturedPhoneNoId = '';
  authCode = '';
  onboardingStep: 'form' | 'meta-pending' | 'confirm' | 'done' = 'form';
  confirmedPhone = '';
  confirmedName = '';
  formError = '';
  isSaving = false;
  onboardDone = false;

  private messageListener?: (e: MessageEvent) => void;

  constructor(
    private route: Router,
    private apiService: ApiRequestsService,
  ) {}

  ngOnInit(): void {
    this.loadOnboardingConfig();
  }

  ngOnDestroy(): void {
    if (this.messageListener) window.removeEventListener('message', this.messageListener);
  }

  goToPage(route: string) {
    this.route.navigateByUrl(route);
  }

  // ── Sign-in ──────────────────────────────────────────────────────────────

  async login() {
    if (!this.clientCode || !this.userName || !this.pwd) {
      this.errorMessage = 'Please enter client code, login id and password.';
      return;
    }
    this.isLoggingIn = true;
    this.errorMessage = '';
    try {
      const res = await this.apiService.postApiCallReturningPromise('user/userLogin', {
        clientCode: this.clientCode,
        userName: this.userName,
        pwd: this.pwd,
        deviceType: 'W',
      });
      if (res && res.errorCode === this.apiService.NO_ERROR) {
        this.apiService.updateOrSetTokenValue(res.Data.accessToken);
        this.apiService.currentUserValue = res.Data;
        this.goToPage('/dashboard');
      } else {
        this.errorMessage = res?.errorDescription || 'Invalid login id or password.';
      }
    } catch {
      this.errorMessage = 'Unable to login. Please check your connection.';
    } finally {
      this.isLoggingIn = false;
    }
  }

  // ── Onboarding ───────────────────────────────────────────────────────────

  private loadOnboardingConfig(): void {
    this.apiService.getApiCall('api/v1/client-onboarding/config').subscribe({
      next: (res: any) => {
        if (res?.errorCode === this.apiService.NO_ERROR) {
          this.metaAppId = res.Data?.appId || '';
          this.metaConfigId = res.Data?.configId || '';
          this.startFbSdkLoad();
        }
      },
      error: () => {},
    });
  }

  private startFbSdkLoad(): void {
    if ((window as any).FB) { this.fbSdkLoaded = true; this.tryInitFb(); return; }
    const self = this;
    (window as any).fbAsyncInit = function () { self.fbSdkLoaded = true; self.tryInitFb(); };
    if (!document.getElementById('facebook-jssdk')) {
      const s = document.createElement('script');
      s.id = 'facebook-jssdk';
      s.src = 'https://connect.facebook.net/en_US/sdk.js';
      s.async = true; s.defer = true;
      document.body.appendChild(s);
    }
  }

  private tryInitFb(): void {
    if (!this.fbSdkLoaded || !this.metaAppId || this.fbInitialized) return;
    (window as any).FB.init({ appId: this.metaAppId, autoLogAppEvents: true, xfbml: true, version: 'v21.0' });
    this.fbInitialized = true;
  }

  launchMetaSignup(): void {
    this.formError = '';
    if (!this.newClientCode.trim()) { this.formError = 'Client code is required.'; return; }
    if (!this.newDbName.trim()) { this.formError = 'Database name is required.'; return; }
    if (!this.fbInitialized) { this.formError = 'Meta SDK not ready yet. Please wait.'; return; }

    this.capturedWabaId = '';
    this.capturedPhoneNoId = '';

    this.messageListener = (event: MessageEvent) => {
      if (!event.origin.includes('facebook.com')) return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type === 'WA_EMBEDDED_SIGNUP' && data?.event === 'FINISH') {
          this.capturedWabaId = data.data?.waba_id || '';
          this.capturedPhoneNoId = data.data?.phone_number_id || '';
        }
      } catch {}
    };
    window.addEventListener('message', this.messageListener);

    this.onboardingStep = 'meta-pending';

    (window as any).FB.login((response: any) => {
      if (response.authResponse?.code) {
        this.authCode = response.authResponse.code;
        this.onboardingStep = 'confirm';
      } else {
        this.onboardingStep = 'form';
        this.formError = 'Meta authorization cancelled or failed.';
      }
    }, {
      config_id: this.metaConfigId,
      response_type: 'code',
      override_default_response_type: true,
      extras: { featureName: 'whatsapp_embedded_signup', sessionInfoVersion: 3 },
    });
  }

  confirmAndSave(): void {
    this.formError = '';
    if (!this.capturedWabaId.trim() || !this.capturedPhoneNoId.trim()) {
      this.formError = 'WABA ID and Phone Number ID are required.'; return;
    }
    this.isSaving = true;
    this.apiService.postApiCall('api/v1/client-onboarding/complete', {
      criteria: {
        code: this.authCode,
        clientCode: this.newClientCode.trim(),
        dbName: this.newDbName.trim(),
        wabaId: this.capturedWabaId.trim(),
        phoneNoId: this.capturedPhoneNoId.trim(),
      }
    }).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res?.errorCode === this.apiService.NO_ERROR) {
          this.confirmedPhone = res.Data?.displayPhoneNumber || '';
          this.confirmedName = res.Data?.verifiedName || '';
          this.onboardingStep = 'done';
        } else {
          this.formError = res?.errorDescription || 'Onboarding failed.';
        }
      },
      error: () => { this.isSaving = false; this.formError = 'Network error. Please try again.'; },
    });
  }

  resetOnboarding(): void {
    this.newClientCode = ''; this.newDbName = ''; this.capturedWabaId = '';
    this.capturedPhoneNoId = ''; this.authCode = ''; this.formError = '';
    this.onboardingStep = 'form';
  }

  switchTab(tab: 'signin' | 'onboard'): void {
    this.activeTab = tab;
    this.errorMessage = '';
    this.formError = '';
  }
}
